/**
 * useResponsive - Hook for responsive design utilities
 *
 * Provides breakpoint detection and responsive values
 */

import { useEffect, useState } from "react"

// Breakpoint values (matching theme)
const breakpoints = {
  xs: 320,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
}

type Breakpoint = keyof typeof breakpoints

export function useBreakpoint() {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>("lg")

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      let currentBreakpoint: Breakpoint = "xs"

      for (const [key, value] of Object.entries(breakpoints)) {
        if (width >= value) {
          currentBreakpoint = key as Breakpoint
        }
      }

      setBreakpoint(currentBreakpoint)
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  return breakpoint
}

export function useIsMobile() {
  const breakpoint = useBreakpoint()
  return breakpoint === "xs" || breakpoint === "sm"
}

export function useIsTablet() {
  const breakpoint = useBreakpoint()
  return breakpoint === "md"
}

export function useIsDesktop() {
  const breakpoint = useBreakpoint()
  return breakpoint === "lg" || breakpoint === "xl" || breakpoint === "2xl"
}

// Responsive value helper
export function useResponsiveValue<T>(
  values: Partial<Record<Breakpoint, T>> & { base?: T },
) {
  const breakpoint = useBreakpoint()

  // Find the value for current breakpoint or fall back to smaller ones
  const breakpointKeys = Object.keys(breakpoints) as Breakpoint[]
  const currentIndex = breakpointKeys.indexOf(breakpoint)

  for (let i = currentIndex; i >= 0; i--) {
    const key = breakpointKeys[i]
    if (values[key] !== undefined) {
      return values[key]
    }
  }

  return values.base
}

// Media query hook
export function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(false)

  useEffect(() => {
    const media = window.matchMedia(query)
    setMatches(media.matches)

    const listener = (event: MediaQueryListEvent) => {
      setMatches(event.matches)
    }

    media.addEventListener("change", listener)
    return () => media.removeEventListener("change", listener)
  }, [query])

  return matches
}
